import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  Box,
  Center,
  HStack,
  RadioCard,
  Text,
  Textarea,
} from "@chakra-ui/react";
import Field from "src/components/field";
import LoadingSpinner from "src/components/Loader/LoadingSpinner";
import HourlyRateForm from "src/components/ProjectForms/HourlyRateForm";
import { OfferForm } from "src/pages/ProjectSetup/types/OfferObject";
import CharacterCounter from "../../components/CharacterCount/CharacterCounter";
import PageIntro from "../../components/PageIntro/PageIntro";
import FixedCostForm from "../../components/ProjectForms/FixedCostForm";
import RetainerForm from "../../components/ProjectForms/RetainerForm";
import { useJobDetailQuery } from "../../lib/endpoints/hire/hire.api";
import { useTypedSelector } from "src/lib/store/store";
import { selectUserState } from "src/lib/features/auth/authSlice";

const workType = [
  { id: "fixedcost", label: "Fixed Cost" },
  { id: "retainer", label: "Retainer" },
  { id: "hourly", label: "Hourly rate", helperText: "Coming soon" },
];

const GiveOffer = ({ onNext }: { onNext: (offer: OfferForm) => void }) => {
  const { user } = useTypedSelector(selectUserState);

  const currency = {
    code: user?.userCompany?.currency ?? "USD",
    symbol: user?.userCompany?.currency_symbol ?? "$",
  };
  const { jobId } = useParams();
  const { jobDetail } = useJobDetailQuery(jobId ?? "");
  const [description, setDescription] = useState<string | undefined>(
    jobDetail?.jobDetails ?? undefined
  );
  const [radioInputSelected, setRadioInputSelected] = useState(
    jobDetail?.payment_type
  );

  // console.log('Job details:', jobDetail)

  useEffect(() => {
    setDescription(jobDetail?.jobDetails ?? undefined);
    setRadioInputSelected(jobDetail?.payment_type);
  }, [jobDetail]);

  useEffect(() => {
    const stopPropagation = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // e.returnValue = "Data will be lost if you leave the page, are you sure?";
    };

    window.addEventListener("beforeunload", stopPropagation);

    return () => {
      window.removeEventListener("beforeunload", stopPropagation);
    };
  }, []);

  if (!jobDetail) {
    return (
      <Center mt={5}>
        <LoadingSpinner />
      </Center>
    );
  }

  return (
    <>
      <Box mt={5}>
        <Field
          label="Enter project details"
          mb={5}
          helperText={
            <Box textAlign="right">
              <CharacterCounter text={description} limit={2000} />
            </Box>
          }
        >
          <Textarea
            name="description"
            id="description"
            placeholder="Enter project description here"
            rows={5}
            p={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            // className="w-full border border-gray-400 rounded-md p-3 resize-none"
            resize="none"
            borderWidth={"thin"}
          />
        </Field>

        {/* WORK TYPE => Moved to Hire Now modal */}
        <Field label="Work Type">
          <RadioCard.Root
            w="full"
            value={radioInputSelected}
            onValueChange={({ value }) =>
              setRadioInputSelected(value ?? undefined)
            }
          >
            {/* <RadioCard.Label
              color="primary"
              fontSize={"md"}
              fontWeight={"semibold"}
              mb={3.5}
            >
              Work Type
            </RadioCard.Label> */}
            <HStack align="stretch" fontWeight={"normal"}>
              {workType.map((item) => (
                <RadioCard.Item
                  borderWidth={"thin"}
                  key={item.id}
                  value={item.id}
                  disabled={true}
                  title={item.helperText}
                  cursor={"not-allowed"}
                  opacity={0.6}
                  bg={radioInputSelected === item.id ? "gray.200" : undefined}
                  _checked={{ ringOffsetColor: "secondary" }}
                >
                  <RadioCard.ItemHiddenInput />
                  <RadioCard.ItemControl>
                    <RadioCard.ItemContent>
                      <RadioCard.ItemText>{item.label}</RadioCard.ItemText>
                    </RadioCard.ItemContent>
                    <RadioCard.ItemIndicator
                      hideBelow={"md"}
                      _checked={{ bg: "secondary" }}
                    />
                  </RadioCard.ItemControl>
                </RadioCard.Item>
              ))}
            </HStack>
          </RadioCard.Root>
        </Field>
      </Box>

      <Text mt={10} fontSize="lg" fontWeight={"semibold"} mb={3.5}>
        {radioInputSelected === "hourly"
          ? "Hourly"
          : radioInputSelected === "fixedcost"
          ? "Fixed Cost"
          : radioInputSelected === "retainer"
          ? "Retainer"
          : ""}
      </Text>

      {/* Render different forms based on selected radio button */}
      {radioInputSelected === "hourly" && (
        <HourlyRateForm description={description ?? ""} jobDetail={jobDetail} />
      )}

      {radioInputSelected === "fixedcost" && (
        <FixedCostForm
          currencySymbol={currency.symbol}
          description={description ?? ""}
          jobDetail={jobDetail}
          onDone={(data) => {
            onNext(data);
          }}
        />
      )}

      {radioInputSelected === "retainer" && (
        <RetainerForm
          currencySymbol={currency.symbol}
          description={description ?? ""}
          jobDetail={jobDetail}
          onDone={(data) => {
            onNext(data);
          }}
        />
      )}
    </>
  );
};

export default GiveOffer;
